// frontend/src/store/slices/transactionSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { transactionAPI } from "../../services/api";
import i18n from "../../i18n";

// Thunk for fetching transaction list
export const fetchTransactions = createAsyncThunk(
  "transactions/fetchAll",
  async (params, { rejectWithValue }) => {
    try {
      const response = await transactionAPI.getTransactions(params);
      return {
        transactions: response.data.transactions,
        pagination: response.data.pagination,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          i18n.t("errors.fetchTransactionsFailed")
      );
    }
  }
);

// Thunk for fetching single transaction details
export const fetchTransactionDetails = createAsyncThunk(
  "transactions/fetchDetails",
  async (transactionId, { rejectWithValue }) => {
    try {
      const response = await transactionAPI.getTransactionDetails(
        transactionId
      );

      // If this transaction has an eSIM, fetch current usage data
      if (
        response.data.type === "esim_purchase" &&
        response.data.ESIMOrder?.iccid
      ) {
        const esimData = await transactionAPI.getESIMDetails(
          response.data.ESIMOrder.iccid
        );
        return {
          ...response.data,
          ESIMOrder: {
            ...response.data.ESIMOrder,
            currentUsage: esimData.data,
          },
        };
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          i18n.t("errors.fetchTransactionDetailsFailed")
      );
    }
  }
);

// Initial state with complete type definition
const initialState = {
  transactions: [],
  selectedTransaction: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    pages: 1,
  },
  loading: {
    list: false,
    details: false,
  },
  error: null,
  filters: {
    type: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  },
  stats: {
    totalTransactions: 0,
    totalAmount: 0,
    successRate: 0,
  },
};

const transactionSlice = createSlice({
  name: "transactions",
  initialState,
  reducers: {
    clearTransactionError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
      // Reset pagination when filters change
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearSelectedTransaction: (state) => {
      state.selectedTransaction = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch transactions list
      .addCase(fetchTransactions.pending, (state) => {
        state.loading.list = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading.list = false;
        state.transactions = action.payload.transactions;
        state.pagination = action.payload.pagination;

        // Calculate stats
        state.stats = {
          totalTransactions: action.payload.pagination.total,
          totalAmount: action.payload.transactions.reduce(
            (sum, transaction) => sum + Number(transaction.amount),
            0
          ),
          successRate:
            action.payload.transactions.filter((t) => t.status === "completed")
              .length / action.payload.transactions.length,
        };
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading.list = false;
        state.error = action.payload;
        state.transactions = [];
      })

      // Fetch transaction details
      .addCase(fetchTransactionDetails.pending, (state) => {
        state.loading.details = true;
        state.error = null;
      })
      .addCase(fetchTransactionDetails.fulfilled, (state, action) => {
        state.loading.details = false;
        state.selectedTransaction = action.payload;

        // Update the transaction in the list if it exists
        const index = state.transactions.findIndex(
          (t) => t.id === action.payload.id
        );
        if (index !== -1) {
          state.transactions[index] = {
            ...state.transactions[index],
            ...action.payload,
          };
        }
      })
      .addCase(fetchTransactionDetails.rejected, (state, action) => {
        state.loading.details = false;
        state.error = action.payload;
        state.selectedTransaction = null;
      });
  },
});

// Selectors
export const selectTransactions = (state) => state.transactions.transactions;
export const selectPagination = (state) => state.transactions.pagination;
export const selectLoading = (state) => state.transactions.loading;
export const selectError = (state) => state.transactions.error;
export const selectFilters = (state) => state.transactions.filters;
export const selectStats = (state) => state.transactions.stats;
export const selectSelectedTransaction = (state) =>
  state.transactions.selectedTransaction;

export const {
  clearTransactionError,
  setFilters,
  clearFilters,
  clearSelectedTransaction,
} = transactionSlice.actions;

export default transactionSlice.reducer;
